import { CALLSENSE_SCORE_FIELDS } from '@utazzo/shared';
import { UzSelect, UzSpinner } from '@utazzo/ui';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Download,
  FileText,
  Flag,
  MessageCircleQuestion,
  MicOff,
  Pause,
  Phone,
  Play,
  Sparkles,
  Target,
  Timer,
  Volume2,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useLeadCalls } from '../use-leads';

import type { LeadCallLogEntry } from '../leads.api';
import type { CallsenseCallRow } from '@utazzo/shared';

import { OUTCOME_LABELS, SCORE_LABELS } from '@/features/callsense/components/callsense-format';
import { useCallsenseCall, useLeadCallInsights } from '@/features/callsense/use-callsense';

// ---------- helpers ----------

/** "2026-08-07 15:59:54" (CallerDesk local time) -> "07 Aug 2026 · 15:59". */
const formatCdDateTime = (v: string | null): string => {
  if (!v) return '-';
  const [d, t] = v.split(' ');
  const [y, m, day] = (d ?? '').split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; // prettier-ignore
  const mon = months[Number(m) - 1];
  const time = (t ?? '').slice(0, 5);
  return mon && day && y ? `${day} ${mon} ${y}${time ? ` · ${time}` : ''}` : v;
};

const formatCallSeconds = (s: number | null): string => {
  if (s == null) return '-';
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const sentimentPill = (sentiment: string): string =>
  sentiment === 'positive'
    ? 'bg-emerald-50 text-emerald-700'
    : sentiment === 'negative'
      ? 'bg-rose-50 text-rose-600'
      : 'bg-teal-50 text-teal-700';

const scoreBarClass = (score: number): string =>
  score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-rose-500';

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const CALL_PERIOD_OPTIONS = [
  { value: '', label: 'All time' },
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
];

const Chip = ({
  children,
  tone = 'zinc',
}: {
  children: React.ReactNode;
  tone?: 'zinc' | 'emerald' | 'blue' | 'amber' | 'rose';
}): React.JSX.Element => {
  const tones: Record<string, string> = {
    zinc: 'bg-zinc-100 text-zinc-700',
    emerald: 'bg-emerald-600 text-white',
    blue: 'bg-blue-600 text-white',
    amber: 'bg-amber-500 text-white',
    rose: 'bg-rose-600 text-white',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

// ---------- provider field grid (ops escape hatch) ----------

const CallFieldGrid = ({ call }: { call: LeadCallLogEntry }): React.JSX.Element => {
  const fields: Array<[string, string | number | null]> = [
    ['Call SID', call.callSid],
    ['Caller name', call.callerName],
    ['Deskphone', call.deskphone],
    ['Member number', call.memberNumber],
    ['Coins deducted', call.coinsDeducted],
    ['Circle', call.circle],
    ['Key pressed', call.keyPressed],
    ['Call result', call.callResult],
    ['Flow type', call.flowType],
    ['Leg A picked', call.legAPickedTime],
    ['Leg B start', call.legBStartTime],
    ['Leg B picked', call.legBPickedTime],
    ['Blacklisted', call.blocked],
    ['Call group', call.callGroupName],
    ['Member type', call.memberType],
    ['Provider row id', call.providerRowId],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 md:grid-cols-3">
      {fields.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
            {label}
          </dt>
          <dd
            className="truncate font-mono text-xs text-zinc-700"
            title={value == null ? undefined : String(value)}
          >
            {value ?? '-'}
          </dd>
        </div>
      ))}
    </dl>
  );
};

// ---------- left column: one recorded-call card ----------

const CallCard = ({
  call,
  index,
  insight,
  selected,
  onSelect,
  onOpenTranscript,
}: {
  call: LeadCallLogEntry;
  index: number;
  insight?: CallsenseCallRow;
  selected: boolean;
  onSelect: () => void;
  onOpenTranscript: () => void;
}): React.JSX.Element => {
  const [showFields, setShowFields] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const connected = (call.talkDurationSeconds ?? 0) > 0;

  const togglePlay = (): void => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect();
      }}
      className={`cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition-all hover:shadow-md ${
        selected ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-zinc-200'
      }`}
    >
      {/* header row */}
      <div className="flex flex-wrap items-center gap-2">
        <Phone className="h-4 w-4 text-emerald-600" />
        <span className="text-base font-bold text-zinc-900">Call {index}</span>
        <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-semibold text-zinc-700">
          {formatCdDateTime(call.startTime ?? call.callDate)}
        </span>
        {(() => {
          const dir =
            insight?.direction && insight.direction !== 'unknown'
              ? insight.direction
              : call.flowType
                ? call.flowType.toUpperCase().includes('OBD')
                  ? 'outbound'
                  : 'inbound'
                : null;
          return dir ? (
            <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
              {dir === 'outbound' ? 'Outgoing' : 'Incoming'}
            </span>
          ) : null;
        })()}
        {insight ? (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${sentimentPill(insight.sentimentCustomer)}`}
          >
            {insight.sentimentCustomer}
          </span>
        ) : (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${connected ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
          >
            {call.callStatus ?? '-'}
          </span>
        )}
        {insight && (insight.escalationRecommended || insight.redFlagCount > 0) && (
          <AlertTriangle className="h-4 w-4 text-rose-500" />
        )}
        <span className="ml-auto flex items-center gap-1 text-[13px] font-medium text-zinc-500">
          <Clock className="h-3.5 w-3.5" />
          {formatCallSeconds(call.talkDurationSeconds ?? call.totalDurationSeconds)} min
        </span>
      </div>

      {/* AI score bar */}
      {insight?.overallScore != null ? (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-100">
            <div
              className={`h-full rounded-full ${scoreBarClass(insight.overallScore)}`}
              style={{ width: `${insight.overallScore}%` }}
            />
          </div>
          <span className="text-[13px] font-bold text-zinc-700 tabular-nums">
            {insight.overallScore.toFixed(0)}%
          </span>
        </div>
      ) : connected ? (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 rounded-full bg-zinc-100" />
          <span className="text-xs font-medium text-zinc-400">AI pending</span>
        </div>
      ) : null}

      {/* chips */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {insight && (
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 ring-inset">
            {OUTCOME_LABELS[insight.callOutcome] ?? insight.callOutcome}
          </span>
        )}
        {insight?.destination && (
          <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-blue-200 ring-inset">
            {insight.destination}
          </span>
        )}
        {insight?.primaryLanguage && (
          <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700 ring-1 ring-violet-100 ring-inset">
            {insight.primaryLanguage}
          </span>
        )}
        {connected && (call.talkDurationSeconds ?? 0) < 60 && (
          <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-600 ring-1 ring-rose-100 ring-inset">
            Short Call
          </span>
        )}
        {insight?.followUpRequired && insight.callOutcome !== 'follow_up_needed' && (
          <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-100 ring-inset">
            Follow-up Needed
          </span>
        )}
        {insight && insight.redFlagCount > 0 && (
          <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-600 ring-1 ring-rose-100 ring-inset">
            Red Flags ({insight.redFlagCount})
          </span>
        )}
        {call.memberName && (
          <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-semibold text-zinc-600">
            SPOC: {call.memberName}
          </span>
        )}
      </div>

      {/* AI summary */}
      {insight?.callReason && (
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-zinc-600">
          {insight.callReason}
        </p>
      )}

      {/* actions: pill buttons, then the full player bar (mock style) */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {call.recordingUrl && connected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (playing) {
                const el = audioRef.current;
                if (el) {
                  el.pause();
                  el.currentTime = 0;
                }
                setCurrentTime(0);
                setPlaying(false);
              } else {
                togglePlay();
              }
            }}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white transition-colors ${
              playing ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-zinc-900 hover:bg-zinc-700'
            }`}
          >
            {playing ? (
              <>
                <span className="h-2.5 w-2.5 rounded-[2px] bg-white" /> Stop
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5" /> Play
              </>
            )}
          </button>
        )}
        {insight && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenTranscript();
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            <FileText className="h-3.5 w-3.5" />
            Transcript
          </button>
        )}
        {call.recordingUrl && (
          <a
            href={call.recordingUrl}
            download
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </a>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowFields((v) => !v);
          }}
          className="ml-auto rounded p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
          title="All CallerDesk fields"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${showFields ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* player bar: round toggle, elapsed, seek, total, volume */}
      {call.recordingUrl && connected && (
        <div
          className="mt-3 flex items-center gap-3 rounded-xl bg-zinc-100 px-3 py-2.5"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <button
            type="button"
            onClick={togglePlay}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white transition-colors hover:bg-emerald-800"
            aria-label={playing ? 'Pause recording' : 'Play recording'}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
          </button>
          <span className="w-10 shrink-0 text-right font-mono text-sm text-zinc-500 tabular-nums">
            {formatCallSeconds(Math.floor(currentTime))}
          </span>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onChange={(e) => {
              const t = Number(e.target.value);
              if (audioRef.current) audioRef.current.currentTime = t;
              setCurrentTime(t);
            }}
            className="h-1.5 min-w-[80px] flex-1 cursor-pointer accent-emerald-700"
            aria-label="Seek recording"
          />
          <span className="w-10 shrink-0 font-mono text-sm text-zinc-500 tabular-nums">
            {formatCallSeconds(Math.floor(duration || call.talkDurationSeconds || 0))}
          </span>
          <Volume2 className="h-4 w-4 shrink-0 text-zinc-500" />
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => {
              const v = Number(e.target.value);
              setVolume(v);
              if (audioRef.current) audioRef.current.volume = v;
            }}
            className="h-1.5 w-20 shrink-0 cursor-pointer accent-emerald-700"
            aria-label="Volume"
          />
          <audio
            ref={audioRef}
            preload="metadata"
            src={call.recordingUrl}
            onEnded={() => setPlaying(false)}
            onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            className="hidden"
          >
            <track kind="captions" />
          </audio>
        </div>
      )}

      {showFields && (
        <div className="mt-3 rounded-xl bg-zinc-50 p-3">
          <CallFieldGrid call={call} />
        </div>
      )}
    </div>
  );
};

/** One parsed transcript turn for the chat-bubble view. */
interface TranscriptTurn {
  side: 'agent' | 'customer' | 'other';
  label: string;
  time: string | null;
  text: string;
}

/**
 * Parse the pipeline's "[mm:ss] Agent: ..." transcript into chat turns.
 * Lines without a speaker prefix continue the previous turn (wrapped
 * sentences). Unknown speakers (IVR / automated messages) render as
 * neutral full-width rows.
 */
const parseTranscript = (raw: string): TranscriptTurn[] => {
  const turns: TranscriptTurn[] = [];
  const line = /^\s*(?:\[(\d{1,2}:\d{2}(?::\d{2})?)\])?\s*([^:\n]{1,40}?)\s*:\s*(.*)$/;
  for (const rawLine of raw.split('\n')) {
    if (!rawLine.trim()) continue;
    const m = line.exec(rawLine);
    if (m && m[3] !== undefined) {
      const speaker = (m[2] ?? '').trim();
      const lower = speaker.toLowerCase();
      const side: TranscriptTurn['side'] = lower.startsWith('agent')
        ? 'agent'
        : lower.startsWith('customer')
          ? 'customer'
          : 'other';
      turns.push({ side, label: speaker, time: m[1] ?? null, text: m[3].trim() });
    } else if (turns.length > 0) {
      const last = turns[turns.length - 1];
      if (last) last.text += ` ${rawLine.trim()}`;
    } else {
      turns.push({ side: 'other', label: '', time: null, text: rawLine.trim() });
    }
  }
  return turns;
};

/** WhatsApp-style transcript: customer left (grey), agent right (green). */
const TranscriptBubbles = ({
  raw,
  agentName,
}: {
  raw: string;
  agentName: string | null;
}): React.JSX.Element => {
  const turns = parseTranscript(raw);
  if (turns.length < 2) {
    return (
      <pre className="max-h-96 overflow-auto text-[12.5px] leading-relaxed whitespace-pre-wrap text-zinc-600">
        {raw}
      </pre>
    );
  }
  return (
    <div className="max-h-96 space-y-2.5 overflow-auto pr-1">
      {turns.map((turn, i) =>
        turn.side === 'other' ? (
          <div key={i} className="px-6 text-center text-xs text-zinc-400 italic">
            {turn.label ? `${turn.label}: ` : ''}
            {turn.text}
          </div>
        ) : (
          <div
            key={i}
            className={`flex ${turn.side === 'agent' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                turn.side === 'agent'
                  ? 'rounded-br-md bg-emerald-50 text-emerald-900'
                  : 'rounded-bl-md bg-zinc-100 text-zinc-800'
              }`}
            >
              <div
                className={`mb-0.5 flex items-baseline gap-2 text-xs ${
                  turn.side === 'agent' ? 'text-emerald-700/70' : 'text-zinc-400'
                }`}
              >
                <span>{turn.side === 'agent' ? (agentName ?? 'Sales Agent') : 'Customer'}</span>
                {turn.time && <span className="font-mono text-[10px] opacity-70">{turn.time}</span>}
              </div>
              <div className="text-sm leading-relaxed">{turn.text}</div>
            </div>
          </div>
        ),
      )}
    </div>
  );
};

// ---------- right column: AI logs panel ----------

type AiTab = 'conversation' | 'requirements' | 'insights';

const ScoreRow = ({
  label,
  value,
  note,
}: {
  label: string;
  value: number | null;
  note: string | null;
}): React.JSX.Element => (
  <div className="flex items-start justify-between gap-3 border-b border-zinc-100 py-2.5 last:border-b-0">
    <span className="text-sm text-zinc-500">{label}</span>
    <div className="flex-1 text-right">
      <span className="text-base font-bold text-zinc-900 tabular-nums">{value ?? '-'}</span>
      <span className="text-xs text-zinc-400"> / 5</span>
      {note && <div className="text-xs leading-snug text-zinc-500">{note}</div>}
    </div>
  </div>
);

const HealthTile = ({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  tone: 'emerald' | 'blue' | 'amber' | 'rose';
}): React.JSX.Element => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
  } as const;
  return (
    <div className="flex items-center gap-3 rounded-xl bg-zinc-50 p-3">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tones[tone]}`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-base leading-tight font-bold text-zinc-900">{value}</div>
        <div className="truncate text-xs text-zinc-500">{label}</div>
      </div>
    </div>
  );
};

const AiLogsPanel = ({
  leadId,
  insight,
  tab,
  onTabChange,
}: {
  leadId: string;
  insight: CallsenseCallRow | null;
  tab: AiTab;
  onTabChange: (t: AiTab) => void;
}): React.JSX.Element => {
  const detail = useCallsenseCall(insight?.id ?? null, leadId);
  const analysis = (detail.data?.analysis ?? {}) as Record<string, unknown>;
  const rationale = (analysis.score_rationale ?? {}) as Record<string, unknown>;
  const [transcriptLang, setTranscriptLang] = useState<'english' | 'original'>('original');

  const travellers = (analysis.num_travellers ?? null) as {
    adults?: number | null;
    children?: number | null;
    infants?: number | null;
  } | null;

  const requirementChips: string[] = [
    typeof analysis.destination === 'string' ? analysis.destination : null,
    typeof analysis.origin_city === 'string' ? `From ${analysis.origin_city}` : null,
    typeof analysis.travel_dates === 'string' ? analysis.travel_dates : null,
    typeof analysis.duration_nights === 'number' ? `${analysis.duration_nights} nights` : null,
    travellers
      ? `${travellers.adults ?? 0} adult${(travellers.adults ?? 0) === 1 ? '' : 's'}${travellers.children ? `, ${travellers.children} child` : ''}`
      : null,
    typeof analysis.trip_type === 'string' ? analysis.trip_type : null,
    typeof analysis.budget === 'string' ? `Budget: ${analysis.budget}` : null,
    ...asStringArray(analysis.preferences),
    ...asStringArray(analysis.special_requests),
  ].filter((c): c is string => Boolean(c));

  const politePhrases = asStringArray(analysis.used_polite_phrases).slice(0, 4);
  const customerQuestions = asStringArray(analysis.customer_questions);
  // Older analyses predate next_call_focus - fall back to follow_up_actions.
  const nextFocusRaw = asStringArray(analysis.next_call_focus);
  const nextFocus =
    nextFocusRaw.length > 0 ? nextFocusRaw : asStringArray(analysis.follow_up_actions);
  const requirementsCaptured = asStringArray(analysis.requirements_captured);
  const requirementsMissed = asStringArray(analysis.requirements_missed);
  const redFlags = asStringArray(analysis.red_flags);
  const coaching = asStringArray(analysis.coaching_notes);
  const deadAir = asStringArray(analysis.dead_air_or_long_pauses);

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50/30 p-4">
      {/* header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          <span className="text-lg font-bold text-zinc-900">AI logs</span>
        </div>
        {insight?.callDate && (
          <span className="rounded-full border border-zinc-200 bg-white px-2.5 py-0.5 text-xs font-medium text-zinc-500">
            {new Date(insight.callDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
            })}
          </span>
        )}
      </div>
      <p className="mt-1 text-[13px] leading-snug text-zinc-500">
        AI scans call recordings and writes clean notes with key pointers. Select a call on the left
        to review it.
      </p>

      {/* Next-call briefing: what the customer asked + what to focus on
            next. Pinned above the tab content so the agent preps at a
            glance no matter which tab is open. */}
      {(customerQuestions.length > 0 || nextFocus.length > 0) && (
        <div className="mt-3 max-h-[38%] shrink-0 overflow-y-auto rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
          {customerQuestions.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 text-sm font-bold text-blue-900">
                <MessageCircleQuestion className="h-4 w-4 text-blue-600" />
                Customer asked
              </div>
              <ul className="mt-1.5 space-y-1 pl-1 text-sm leading-relaxed text-zinc-700">
                {customerQuestions.map((q, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-blue-400">•</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {nextFocus.length > 0 && (
            <>
              <div
                className={`flex items-center gap-1.5 text-sm font-bold text-emerald-900 ${customerQuestions.length > 0 ? 'mt-3' : ''}`}
              >
                <Target className="h-4 w-4 text-emerald-600" />
                Next follow-up focus
              </div>
              <ul className="mt-1.5 space-y-1 pl-1 text-sm leading-relaxed text-zinc-700">
                {nextFocus.map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-emerald-500">•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* tabs */}
      <div className="mt-3 flex rounded-xl border border-zinc-200 bg-white p-1">
        {(
          [
            ['conversation', 'Conversation'],
            ['requirements', 'Requirements'],
            ['insights', 'AI Insights'],
          ] as Array<[AiTab, string]>
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => onTabChange(value)}
            className={`flex-1 rounded-lg px-2 py-1.5 text-[13px] font-semibold transition-colors ${
              tab === value ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {!insight ? (
        <p className="mt-6 pb-2 text-center text-sm text-zinc-400">
          {'←'} Select an analyzed call to see its AI notes
        </p>
      ) : detail.isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading analysis…
        </div>
      ) : (
        <div className="mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {tab === 'insights' && (
            <>
              {/* headline chips */}
              <div className="flex flex-wrap gap-1.5">
                <Chip tone="emerald">
                  Overall: {OUTCOME_LABELS[insight.callOutcome] ?? insight.callOutcome}
                </Chip>
                <Chip
                  tone={
                    insight.sentimentCustomer === 'positive'
                      ? 'emerald'
                      : insight.sentimentCustomer === 'negative'
                        ? 'rose'
                        : 'blue'
                  }
                >
                  Customer: {insight.sentimentCustomer}
                </Chip>
                {insight.followUpRequired && <Chip tone="blue">Follow-up required</Chip>}
                {insight.escalationRecommended && <Chip tone="rose">Escalation recommended</Chip>}
                {insight.overallScore != null && (
                  <Chip tone="zinc">Score: {insight.overallScore.toFixed(0)}/100</Chip>
                )}
              </div>

              {/* agent tone & delivery */}
              <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="text-[15px] font-bold text-zinc-900">
                  {insight.agentName ?? 'Agent'} - Tone and Delivery (1 to 5)
                </div>
                <div className="mt-2">
                  {CALLSENSE_SCORE_FIELDS.map((field) => {
                    const value =
                      typeof analysis[field] === 'number' ? (analysis[field] as number) : null;
                    const note =
                      typeof rationale[field] === 'string' ? (rationale[field] as string) : null;
                    if (value == null && !note) return null;
                    return (
                      <ScoreRow key={field} label={SCORE_LABELS[field]} value={value} note={note} />
                    );
                  })}
                </div>
                {(politePhrases.length > 0 || typeof analysis.tone_description === 'string') && (
                  <div className="mt-3 rounded-xl bg-zinc-50 p-3">
                    <div className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                      Tone indicators detected
                    </div>
                    {typeof analysis.tone_description === 'string' && analysis.tone_description && (
                      <p className="mt-1.5 text-sm leading-relaxed text-zinc-600 italic">
                        {analysis.tone_description}
                      </p>
                    )}
                    {politePhrases.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {politePhrases.map((phrase) => (
                          <span
                            key={phrase}
                            className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100 ring-inset"
                          >
                            {phrase}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* requirements gathering: what the agent captured vs should have asked */}
              {(requirementsCaptured.length > 0 || requirementsMissed.length > 0) && (
                <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                  <div className="text-[15px] font-bold text-zinc-900">Requirements gathering</div>
                  {requirementsCaptured.length > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center gap-1.5 text-sm font-bold text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Captured on this call
                      </div>
                      <ul className="mt-1 space-y-1 text-[13px] leading-relaxed text-zinc-600">
                        {requirementsCaptured.map((item, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {requirementsMissed.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1.5 text-sm font-bold text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5" /> Should have asked
                      </div>
                      <ul className="mt-1 space-y-1 text-[13px] leading-relaxed text-zinc-600">
                        {requirementsMissed.map((item, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* red flags + coaching */}
              {(redFlags.length > 0 || coaching.length > 0) && (
                <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                  {redFlags.length > 0 && (
                    <>
                      <div className="flex items-center gap-1.5 text-sm font-bold text-rose-600">
                        <AlertTriangle className="h-3.5 w-3.5" /> Red flags
                      </div>
                      <ul className="mt-1 mb-2 list-disc pl-5 text-[13px] text-zinc-600">
                        {redFlags.map((flag, i) => (
                          <li key={i}>{flag}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {coaching.length > 0 && (
                    <>
                      <div className="text-sm font-bold text-zinc-900">Coaching notes</div>
                      <ul className="mt-1 list-disc pl-5 text-[13px] text-zinc-600">
                        {coaching.map((note, i) => (
                          <li key={i}>{note}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {/* conversation health */}
              <div className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="text-[15px] font-bold text-zinc-900">
                  Conversation Health Overview
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <HealthTile
                    icon={Clock}
                    tone="emerald"
                    label="Talk time"
                    value={
                      insight.durationMinutes != null
                        ? `${insight.durationMinutes.toFixed(1)} min`
                        : '-'
                    }
                  />
                  <HealthTile
                    icon={MicOff}
                    tone="blue"
                    label="Interruptions"
                    value={
                      typeof analysis.interruptions_count === 'number'
                        ? analysis.interruptions_count
                        : '-'
                    }
                  />
                  <HealthTile icon={Timer} tone="amber" label="Dead air" value={deadAir.length} />
                  <HealthTile
                    icon={Flag}
                    tone="rose"
                    label="Red flags"
                    value={insight.redFlagCount}
                  />
                </div>
              </div>
            </>
          )}

          {tab === 'requirements' && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-4">
              <div className="text-[15px] font-bold text-zinc-900">Requirements snapshot</div>
              {requirementChips.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {requirementChips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full bg-zinc-100 px-2.5 py-1 text-[13px] font-semibold text-zinc-700"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-[13px] text-zinc-500">
                  No travel requirements were mentioned on this call.
                </p>
              )}
              {typeof analysis.quoted_price === 'string' && analysis.quoted_price && (
                <p className="mt-3 text-[13px] text-zinc-600">
                  <span className="font-semibold">Quoted:</span> {analysis.quoted_price}
                </p>
              )}
              {typeof analysis.booking_status === 'string' && analysis.booking_status && (
                <p className="mt-1 text-[13px] text-zinc-600">
                  <span className="font-semibold">Booking status:</span> {analysis.booking_status}
                </p>
              )}
              {asStringArray(analysis.follow_up_actions).length > 0 && (
                <>
                  <div className="mt-3 text-[13px] font-bold text-zinc-900">Follow-up actions</div>
                  <ul className="mt-1 list-disc pl-5 text-[13px] text-zinc-600">
                    {asStringArray(analysis.follow_up_actions).map((action, i) => (
                      <li key={i}>{action}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {tab === 'conversation' && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-4">
              {typeof analysis.summary === 'string' && analysis.summary && (
                <p className="mb-3 text-sm leading-relaxed text-zinc-700">{analysis.summary}</p>
              )}
              <div className="mb-2 flex gap-1 rounded-lg bg-zinc-100 p-0.5">
                {(['original', 'english'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setTranscriptLang(lang)}
                    className={`flex-1 rounded-md px-2 py-1 text-xs font-semibold capitalize transition-colors ${
                      transcriptLang === lang ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              {typeof analysis[
                transcriptLang === 'english' ? 'transcript_english' : 'transcript_original'
              ] === 'string' ? (
                <TranscriptBubbles
                  raw={
                    analysis[
                      transcriptLang === 'english' ? 'transcript_english' : 'transcript_original'
                    ] as string
                  }
                  agentName={insight.agentName}
                />
              ) : (
                <p className="text-sm text-zinc-400">Transcript not available.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- the tab ----------

/**
 * Lead "Calls" tab - Calyzer-style call recorder + AI notes. Left: recorded
 * call cards (live CallerDesk CDRs enriched with CallSense analysis). Right:
 * the AI logs panel for the selected call (conversation / requirements /
 * insights). Provider raw fields stay reachable per-card via the chevron.
 */
export const CallAnalysisTab = ({ leadId }: { leadId: string }): React.JSX.Element => {
  const query = useLeadCalls(leadId);
  const allCalls = useMemo(() => query.data?.calls ?? [], [query.data]);

  const sids = useMemo(
    () => [...new Set(allCalls.map((c) => c.callSid).filter(Boolean))],
    [allCalls],
  );
  const insightsQuery = useLeadCallInsights(leadId, sids);
  const insights = useMemo(() => insightsQuery.data ?? {}, [insightsQuery.data]);

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [aiTab, setAiTab] = useState<AiTab>('insights');

  // filters
  const [agentFilter, setAgentFilter] = useState('');
  // Default to connected calls - missed/cancelled legs are noise for review.
  const [outcomeFilter, setOutcomeFilter] = useState('connected');
  const [periodFilter, setPeriodFilter] = useState('');

  const agentOptions = useMemo(() => {
    const names = [...new Set(allCalls.map((c) => c.memberName).filter(Boolean))] as string[];
    return [
      { value: '', label: 'All agents' },
      ...names.sort().map((n) => ({ value: n, label: n })),
    ];
  }, [allCalls]);

  const calls = useMemo(() => {
    let cutoff: string | null = null;
    if (periodFilter) {
      const days = Number(periodFilter);
      const d = new Date(Date.now() - (days - 1) * 86_400_000);
      const pad = (n: number): string => String(n).padStart(2, '0');
      cutoff = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
    }
    return allCalls.filter((c) => {
      if (agentFilter && c.memberName !== agentFilter) return false;
      if (cutoff && (c.startTime ?? c.callDate ?? '') < cutoff) return false;
      if (outcomeFilter === 'connected' && (c.talkDurationSeconds ?? 0) <= 0) return false;
      if (outcomeFilter === 'missed' && (c.talkDurationSeconds ?? 0) > 0) return false;
      if (outcomeFilter === 'analyzed' && !(c.callSid && insights[c.callSid])) return false;
      return true;
    });
  }, [allCalls, agentFilter, outcomeFilter, periodFilter, insights]);

  // Auto-select on load: the latest analyzed call (falls back to the
  // latest call) so the AI panel is populated without a click.
  useEffect(() => {
    if (selectedRowId && calls.some((c) => c.providerRowId === selectedRowId)) return;
    const firstAnalyzed = calls.find((c) => c.callSid && insights[c.callSid]);
    const pick = firstAnalyzed ?? calls[0];
    if (pick) setSelectedRowId(pick.providerRowId);
  }, [calls, insights, selectedRowId]);

  const selectedCall = calls.find((c) => c.providerRowId === selectedRowId) ?? null;
  const selectedInsight =
    selectedCall?.callSid != null ? (insights[selectedCall.callSid] ?? null) : null;

  const filtersActive = Boolean(agentFilter || outcomeFilter !== 'connected' || periodFilter);
  const connected = calls.filter((c) => (c.talkDurationSeconds ?? 0) > 0);
  const scored = calls
    .map((c) => (c.callSid ? insights[c.callSid]?.overallScore : null))
    .filter((s): s is number => s != null);

  if (query.isLoading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading call history from CallerDesk…
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <p className="text-sm text-rose-600">
          Could not load call history from CallerDesk. It may not be configured, or the provider is
          unreachable - try again in a moment.
        </p>
      </div>
    );
  }

  if (allCalls.length === 0) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <p className="text-sm text-zinc-500">
          No CallerDesk calls found for this lead. Calls appear here after someone uses the Call
          button (or dials this number from the CallerDesk dashboard).
        </p>
      </div>
    );
  }

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
      {/* LEFT: recorded calls */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-lg font-bold text-zinc-900">
            Recorded calls
            <span className="ml-2 text-sm font-medium text-zinc-400">
              {filtersActive ? `${calls.length} of ${allCalls.length}` : allCalls.length}
            </span>
          </div>
          <span className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
            Linked to this lead
          </span>
        </div>
        <p className="mt-1 text-[13px] text-zinc-500">
          Calls are captured live from CallerDesk. New entries appear at the top with AI tags and
          quick actions.
        </p>

        {/* filters + mini report */}
        <div className="mt-3 flex flex-wrap items-end gap-2">
          {agentOptions.length > 2 && (
            <UzSelect
              options={agentOptions}
              value={agentFilter}
              onValueChange={setAgentFilter}
              containerClassName="w-40"
              triggerClassName="h-8 text-xs"
            />
          )}
          <UzSelect
            options={[
              { value: '', label: 'All calls' },
              { value: 'connected', label: 'Connected' },
              { value: 'missed', label: 'Not connected' },
              { value: 'analyzed', label: 'AI analyzed' },
            ]}
            value={outcomeFilter}
            onValueChange={setOutcomeFilter}
            containerClassName="w-40"
            triggerClassName="h-8 text-xs"
          />
          <UzSelect
            options={CALL_PERIOD_OPTIONS}
            value={periodFilter}
            onValueChange={setPeriodFilter}
            containerClassName="w-36"
            triggerClassName="h-8 text-xs"
          />
          {filtersActive && (
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-zinc-500 hover:bg-zinc-100"
              onClick={() => {
                setAgentFilter('');
                setOutcomeFilter('connected');
                setPeriodFilter('');
              }}
            >
              <XCircle className="h-3.5 w-3.5" /> Clear
            </button>
          )}
          <div className="ml-auto flex flex-wrap gap-1.5 text-xs">
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
              Connected {connected.length}/{calls.length}
            </span>
            {scored.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 font-semibold text-violet-700">
                <Sparkles className="h-3 w-3" />
                Avg score {(scored.reduce((a, b) => a + b, 0) / scored.length).toFixed(0)}/100
              </span>
            )}
          </div>
        </div>

        {/* call cards */}
        <div className="mt-4 space-y-3">
          {calls.length === 0 ? (
            <p className="py-4 text-sm text-zinc-500">No calls match the current filters.</p>
          ) : (
            calls.map((call, idx) => (
              <CallCard
                key={call.providerRowId}
                call={call}
                index={calls.length - idx}
                insight={call.callSid ? insights[call.callSid] : undefined}
                selected={selectedRowId === call.providerRowId}
                onSelect={() => setSelectedRowId(call.providerRowId)}
                onOpenTranscript={() => {
                  setSelectedRowId(call.providerRowId);
                  setAiTab('conversation');
                }}
              />
            ))
          )}
        </div>
      </div>

      {/* RIGHT: AI logs (sticky on wide screens) */}
      <div className="xl:sticky xl:top-4 xl:h-[calc(100vh-2rem)] xl:self-start">
        <AiLogsPanel leadId={leadId} insight={selectedInsight} tab={aiTab} onTabChange={setAiTab} />
      </div>
    </div>
  );
};
